/**
 * Ranges on the register: members of a variant group are ordinary products,
 * shown as one collapsed tile that opens to its flavours or colours.
 */

/** At or under this many units a product is called low — the tile's own rule. */
export const lowStockLimit = (lowStockThreshold) =>
  Math.max(3, Math.round(lowStockThreshold / 3));

export const buildGroupIndex = (groups = []) =>
  new Map(groups.map((group) => [group.id, group]));

export const membersByGroup = (products) => {
  const members = new Map();

  products.forEach((product) => {
    if (!product.variant_group_id) return;

    if (!members.has(product.variant_group_id)) {
      members.set(product.variant_group_id, []);
    }
    members.get(product.variant_group_id).push(product);
  });

  return members;
};

const tileKeyOf = (product, groupIndex) =>
  groupIndex.has(product.variant_group_id)
    ? `group:${product.variant_group_id}`
    : `product:${product.id}`;

/** How many tiles a list of products draws once ranges are collapsed. */
export const countTiles = (products, groupIndex) =>
  new Set(products.map((product) => tileKeyOf(product, groupIndex))).size;

/** Tile counts per chip: { [chipId]: number }, skipping products with no value. */
export const countTilesBy = (products, valueOf, groupIndex) => {
  const seen = {};

  products.forEach((product) => {
    const value = valueOf(product);
    if (!value) return;

    if (!seen[value]) seen[value] = new Set();
    seen[value].add(tileKeyOf(product, groupIndex));
  });

  return Object.fromEntries(
    Object.entries(seen).map(([value, keys]) => [value, keys.size]),
  );
};

/** One tile per product, or per range at the place its first member appears. */
export const toTiles = (products, groupIndex, members, { collapse }) => {
  if (!collapse) {
    return products.map((product) => ({
      type: "product",
      key: product.id,
      product,
    }));
  }

  const seen = new Set();
  const tiles = [];

  products.forEach((product) => {
    const group = groupIndex.get(product.variant_group_id);

    if (!group) {
      tiles.push({ type: "product", key: product.id, product });
      return;
    }

    if (seen.has(group.id)) return;
    seen.add(group.id);

    tiles.push({
      type: "group",
      key: `group-${group.id}`,
      group,
      members: members.get(group.id) || [],
    });
  });

  return tiles;
};

/**
 * What a collapsed tile says about its members: combined stock, how many need
 * attention (low or out), and a price that admits when flavours differ.
 */
export const groupStats = (members, lowStockThreshold) => {
  const limit = lowStockLimit(lowStockThreshold);
  const prices = members.map((member) => Number(member.unit_price));

  const total = members.reduce(
    (sum, member) => sum + Math.max(0, Number(member.stock_quantity) || 0),
    0,
  );

  return {
    count: members.length,
    total,
    allOut: members.every((member) => member.stock_quantity <= 0),
    needAttention: members.filter((member) => member.stock_quantity <= limit)
      .length,
    lowestPrice: prices.length ? Math.min(...prices) : 0,
    pricesDiffer: new Set(prices).size > 1,
    hasOffer: members.some((member) => member.offer),
  };
};
